/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import throttle from 'lodash/throttle';

import { notificationStore } from 'stores';
import { authService } from 'services';
import { version } from 'utils/version';
import {
  ASSET_REFRESH_DELAY,
  ERROR_REASON_MAINTENANCE,
  HTTP_DEBOUNCE_DELAY,
  STATUS_ASSET_REFRESH,
  STATUS_UNAUTHORIZED
} from 'utils/constants';
import { localStorage } from 'utils/localstorage';
import { STATUS_SERVICE_UNAVAILABLE } from 'Server/util/shared-utils';
import { config } from 'utils/config';
import { assignUrl, reload } from 'utils/location';

import trackingService from './TrackingService';
import tracingService from './TracingService';

const FORCE_RELOGIN_MESSAGE = 'Your session expired. You will be logged back in automatically.';
const STATUS_ASSET_REFRESH_MESSAGE = 'The Modeler is updating. Please wait just a few seconds for updates to complete.';

/**
 * The base service class, which all other services inherit their methods from.
 */
export default class Service {
  // Each specialized service can define its own long-standing endpoints
  LONG_STANDING_ENDPOINTS = [];

  /**
   * Sends a request to the backend using the Fetch API. The returned response
   * will be parsed as JSON if applicable.
   * The returned promise resolves if the status code is between 200 and 299. Otherwise,
   * the promise will reject with the status code and some additional information.
   *
   * @param {String} method The HTTP method.
   * @param {String} path The URL path of the endpoint.
   * @param {Object} [body] The optional body with request options.
   * @param {String} [contentType] Optional content type of the request.
   * @param {Function} [onFilesUploadProgress] callback function providing the updated progress percentage
   * @param {AbortSignal} [signal] Optional signal allowing caller to cancel requests that aren't long-running
   * @returns {Promise}
   */
  request(method, path, body, contentType, onFilesUploadProgress, signal = null) {
    return new Promise((resolve, reject) => {
      authService.getToken().then((token) => {
        const baseUrl = config.modelerUrl;
        const url = this.getPreparedURL(`${baseUrl}${path}`);

        if (contentType?.includes('multipart/')) {
          // Handle multipart requests with XMLHttpRequest
          const xhr = new XMLHttpRequest();

          xhr.open(method, url, true);

          // Set headers
          // @ts-expect-error TS2339
          const { headers } = Service.httpOptions(token, contentType);
          Object.keys(headers).forEach((key) => {
            xhr.setRequestHeader(key, headers[key]);
          });

          // Track upload progress
          if (xhr.upload && body instanceof FormData && typeof body.getAll === 'function') {
            xhr.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                onFilesUploadProgress?.(progress);
              }
            };
          }

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                let responseBody = JSON.parse(xhr.responseText);
                responseBody = responseBody.data ? responseBody.data : responseBody;
                resolve(responseBody);
              } catch (error) {
                resolve(xhr.responseText);
                tracingService.traceError(error);
              }
            } else {
              reject({
                status: xhr.status,
                message: xhr.statusText,
                response: xhr.responseText
              });
            }
          };

          xhr.onerror = () => {
            reject({
              status: xhr.status,
              message: 'Network error'
            });
          };

          xhr.send(body);
        } else {
          // Handle non-multipart requests with Fetch API
          const fetchOptions = {
            method,
            body: JSON.stringify(body),
            // @ts-expect-error TS2698
            ...Service.httpOptions(token, contentType)
          };

          if (this.#isLongStandingRequest(path)) {
            fetchOptions.signal = AbortSignal.timeout(config.apiTimeout);
          } else if (signal) {
            fetchOptions.signal = signal;
          }

          fetch(url, fetchOptions)
            .then(async (response) => {
              if (response.status === 408) {
                return reject({
                  status: 408,
                  reason: 'Request timeout'
                });
              } else if (response.status === STATUS_ASSET_REFRESH) {
                handleAppRefresh();
                // NOTE: We don't want to resolve with a response that can have side-effects in the consumer
                // this because an updated backend will return data that is incompatible with an outdated frontend
                return;
              } else if (await this.isRestapiInMaintenanceMode(response)) {
                this.handleRestapiInMaintenanceMode(response);
                // reject with custom object (instead of response) because the error reason is also checked in
                // AuthService.saas.js but can only be consumed from the response body once
                return reject({
                  status: STATUS_SERVICE_UNAVAILABLE,
                  reason: ERROR_REASON_MAINTENANCE
                });
              } else if (response.status === STATUS_UNAUTHORIZED && !path.startsWith('/internal-api/shares/')) {
                this.handleUnauthorized();
                return reject(response);
              } else {
                // @ts-expect-error TS2345
                const responseBody = await this.getResponseBody(response);

                if (response.ok) {
                  return resolve(responseBody);
                } else {
                  this.logError(response);
                  return reject({ ...responseBody, status: response.status });
                }
              }
            })
            .catch((error) => {
              if (error.message?.includes('signal timed out')) {
                return reject({
                  status: 408,
                  reason: 'Request timeout'
                });
              }

              if (!signal?.aborted) {
                tracingService.traceError(error);
              }

              return reject(error);
            });
        }
      });
    });
  }

  /**
   * Sends a GET request to the backend.
   *
   * @param {String} path The URL path of the endpoint.
   * @param {String} [contentType] Optional content type of the request.
   * @param {AbortSignal} [signal] Optional signal allowing caller to cancel requests that aren't long-running
   * @returns {Promise}
   */
  get(path, contentType, signal = null) {
    return this.request('GET', path, undefined, contentType, undefined, signal);
  }

  /**
   * Sends a POST request to the backend.
   *
   * @param {String} path The URL path of the endpoint.
   * @param {Object} [body] The optional body with request options.
   * @param {String} [contentType] Optional content type of the request.
   * @param {Function} [onFilesUploadProgress] callback function providing the updated progress percentage
   * @param {AbortSignal} [signal] Optional signal allowing caller to cancel requests that aren't long-running
   * @returns {Promise}
   */
  post(path, body, contentType, onFilesUploadProgress, signal = null) {
    return this.request('POST', path, body, contentType, onFilesUploadProgress, signal);
  }

  /**
   * Sends a PATCH request to the backend.
   *
   * @param {String} path The URL path of the endpoint.
   * @param {Object} [body] The optional body with request options.
   * @param {String} [contentType] Optional content type of the request.
   * @returns {Promise}
   */
  patch(path, body, contentType) {
    return this.request('PATCH', path, body, contentType);
  }

  /**
   * Sends a PUT request to the backend.
   * @param {String} path The URL path of the endpoint.
   * @param {Object} [body] The optional body with request options.
   * @param {String} [contentType] Optional content type of the request.
   * @returns {Promise}
   */
  put(path, body, contentType) {
    return this.request('PUT', path, body, contentType);
  }

  /**
   * Sends a DELETE request to the backend.
   *
   * @param {String} path The URL path of the endpoint.
   * @param {Object} [body] The optional body with request options.
   * @returns {Promise}
   */
  delete(path, body) {
    return this.request('DELETE', path, body);
  }

  /**
   * Logs an HTTP error to the tracing service. Not all errors are logged,
   * but only 400, 408, 422, and 429.
   *
   * @param {Object} response The response that came back from the backend.
   * @param customMessage Add additional context to the error logged
   * @returns {void}
   */
  logError(response, customMessage) {
    if ([400, 408, 422, 429].includes(response.status)) {
      const error = new Error(
        `Unexpected HTTP error occurred: ${response.url} ${response.status} ${response.statusText} ${JSON.stringify(response.body)}`
      );
      tracingService.traceError(error, customMessage);
    }
  }

  /**
   * Parses and returns the response body from the backend. If possible, it returns
   * the data as JSON.
   *
   * @param {Promise} response The Fetch API's response promise.
   * @ts-expect-error TS1064 returns {Promise|void}
   */
  async getResponseBody(response) {
    try {
      // @ts-expect-error TS2339
      const contentType = response.headers.get('Content-Type');
      // @ts-expect-error TS2339
      const contentDisposition = response.headers.get('Content-Disposition');

      if (contentType?.includes('application/json')) {
        // @ts-expect-error TS2339
        const json = await response.json();

        // @ts-expect-error TS2339
        if (response.ok) {
          return json.data ? json.data : json;
        }

        return json.errors ? json.errors : json;
      } else if (
        contentDisposition?.startsWith('attachment') ||
        contentType?.includes('image/png') ||
        contentType?.includes('image/jpeg') ||
        contentType?.includes('application/pdf')
      ) {
        // Handle downloading blob content, e.g., a file
        // @ts-expect-error TS2339
        return await response.blob();
      } else {
        // @ts-expect-error TS2339
        return await response.text();
      }
    } catch (ex) {
      tracingService.traceError(ex);
      return undefined;
    }
  }

  /**
   * Forces a user to log in again when the session is expired.
   */
  handleUnauthorized() {
    notificationStore.showNotification({
      message: FORCE_RELOGIN_MESSAGE
    });
    trackingService.trackForceRelogin();
    assignUrl(
      window.location.pathname
        ? `${config.modelerUrl}/login?returnUrl=${encodeURIComponent(window.location.pathname)}`
        : `${config.modelerUrl}/login`
    );
  }

  /**
   * Returns a fully built URL, containing search parameters for the asset
   * refresh.
   *
   * @param {String} path The URL path to visit.
   * @returns {String}
   */
  getPreparedURL(path) {
    const url = new URL(path);
    const now = Math.round(new Date().getTime() / 1000);

    try {
      const lastRefresh = Number(localStorage.getItem('lastRefresh') || now);
      const lastRefreshAge = now - lastRefresh;

      url.searchParams.append('version', version);
      // @ts-expect-error TS2345
      url.searchParams.append('lastRefreshAge', lastRefreshAge);
    } catch (ex) {
      console.info("Can't access `localStorage` due to Chrome's CSP.", ex);
    }

    return url.toString();
  }

  /**
   * Returns true if the restapi is in maintenance mode.
   * @param {Object} response The error response with status and body received from the restapi
   * @ts-expect-error TS1064 returns {Boolean}
   */
  async isRestapiInMaintenanceMode(response) {
    if (response.status === STATUS_SERVICE_UNAVAILABLE) {
      const errors = await this.getResponseBody(response);
      return errors && errors[0]?.reason?.includes(ERROR_REASON_MAINTENANCE);
    }
    return false;
  }

  /**
   * Redirects to the maintenance page.
   * @param {Object} response The error response with status and body received from the restapi
   */
  handleRestapiInMaintenanceMode(response) {
    this.logError(response, `restapi is in maintenance mode.`);
    assignUrl(`${config.modelerUrl}/maintenance?returnUrl=${encodeURIComponent(window.location.pathname)}`);
  }

  /**
   * Returns the default HTTP options, like headers.
   *
   * @returns {String}
   */
  static httpOptions(token, contentType) {
    const options = {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json; charset=utf-8',
        'Cache-control': 'no-cache',
        Pragma: 'no-cache',
        Expires: 0,
        'X-Client-Version': version
      }
    };

    /**
     * This is done to allow http client to add the `boundary` directive which is calculated on the fly.
     * For more info see https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Type#directives
     */
    if (!contentType?.includes('multipart/')) {
      options.headers['Content-Type'] = contentType || 'application/json; charset=utf-8';
    }

    if (token) {
      options.headers.Authorization = `Bearer ${token}`;
    }

    // @ts-expect-error TS2322
    return options;
  }

  /**
   * Returns the debounce delay we use for debouncing some critical http calls.
   *
   * @returns {Number}
   */
  static get debounceDelay() {
    return HTTP_DEBOUNCE_DELAY;
  }

  /**
   * Returns the debounce options we use for debouncing some critical http calls.
   *
   * @returns {Object}
   */
  static get debounceOptions() {
    return { leading: true, trailing: false };
  }

  #isLongStandingRequest(url) {
    return this.LONG_STANDING_ENDPOINTS.some((regex) => regex.test(url));
  }
}

/**
 * Reloads the React client.
 */
const handleAppRefresh = throttle(
  () => {
    notificationStore.showNotification({
      variant: 'info',
      message: STATUS_ASSET_REFRESH_MESSAGE
    });

    trackingService.trackAssetRefresh();

    setTimeout(() => {
      const now = Math.round(new Date().getTime() / 1000);
      localStorage.setItem('lastRefresh', now);

      reload();
    }, ASSET_REFRESH_DELAY);
  },
  ASSET_REFRESH_DELAY,
  {
    trailing: false
  }
);
